// src/components/shops/postTemplates/Editorial.tsx
//
// The announcement template: a tall photo, a serif headline and room to
// breathe. This is the one that makes a shop look established, and it flatters
// almost any content — which is why it is the second a Regular shop unlocks
// rather than something more charming and more easily misused.

import { cachedUrl } from '../../../lib/storageUrl'
import SocialBrewBadge from '../SocialBrewBadge'
import { categoryLabel, type TemplateProps } from './types'

export default function Editorial({
  title, body, photos, category, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const label = categoryLabel(category) || 'From the shop'
  const single = photos.length === 1

  return (
    <div
      className="overflow-hidden"
      style={{
        background: 'linear-gradient(170deg, #fdfaf5 0%, #f7efe1 100%)',
        borderRadius: px(2),
        boxShadow: `0 ${px(1)} ${px(3)} rgba(36,24,16,0.12)`,
      }}
      data-shop={shopName}
    >
      {photos.length > 0 && (
        single ? (
          // A tall crop is what gives this template its magazine feel; a wide
          // one would make it just another card.
          <div style={{ aspectRatio: '4 / 5' }}>
            <img src={cachedUrl(photos[0])} alt="" loading="lazy" className="w-full h-full object-cover" />
          </div>
        ) : (
          // Several photos become a tight square block so the headline still
          // leads rather than competing with a tall stack of pictures.
          <div
            className="grid"
            style={{ gridTemplateColumns: '1fr 1fr', gap: px(2), aspectRatio: '1 / 1' }}
          >
            {photos.slice(0, 4).map((url, i) => (
              <div
                key={i}
                className="overflow-hidden"
                // Three photos would otherwise leave an empty quarter, so the
                // first takes the whole top row and the block stays solid.
                style={{ gridColumn: photos.length === 3 && i === 0 ? 'span 2' : undefined }}
              >
                <img src={cachedUrl(url)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )
      )}

      <div style={{ padding: `${px(18)} ${px(16)} ${px(16)}` }}>
        <p
          className="text-caramel font-bold uppercase"
          style={{ fontSize: px(9.5), letterSpacing: '0.2em', marginBottom: px(8) }}
        >
          {label}
        </p>
        <h3
          className="font-display font-bold text-coffee-900"
          style={{ fontSize: px(23), lineHeight: 1.12, letterSpacing: '-0.015em', margin: 0, textWrap: 'balance' }}
        >
          {title}
        </h3>
        {body && (
          <p
            className="text-coffee-500 leading-relaxed"
            style={{ fontSize: px(13), marginTop: px(9) }}
          >
            {body}
          </p>
        )}

        <div style={{ marginTop: px(16) }}>
          <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="light" scale={scale} />
        </div>
      </div>
    </div>
  )
}
